// DraftComplete.js
// Mirrors DraftCompleteView.swift + DraftCompleteComponents.swift exactly.
//
// Layout (top to bottom):
//   Header (back arrow + wordmark)
//   TeamRatingSection: CircularScoreDisplay (left) + chemistry panel (right)
//   Condensed 2-col roster grid
//   High/Low player section
//   Action buttons: full-width primary + 2x2 grid

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toBlob } from "html-to-image";
import wordmark from "../assets/Rosterable.png";
import rankingIcon from "../assets/ranking-fill.svg";
import houseIcon from "../assets/house-fill.svg";
import { useAuth } from "../managers/AuthContext";
import { submitDraftScore, hasSubmittedDraftToday } from "../managers/leaderboardService";
import { SPORT_CONFIG } from "../managers/draftManager";
import {
  getChemistryBreakdown,
  calculateTeamRatingWithChemistry,
  calculateTeamRating,
} from "../managers/teamRatingCalculator";
import { loadTeamLogos } from "../managers/teamLogoService";
import {
  TeamRatingSection,
  RosterCardCondensed,
  getOrderedPlayerIndices,
} from "../components/DraftResult";

// Sport-specific emoji used in the shareable result text.
const SPORT_EMOJI = {
  football:   "🏈",
  baseball:   "⚾",
  basketball: "🏀",
  hockey:     "🏒",
  soccer:     "⚽",
};

// Order players for the 2-column roster grid: see getOrderedPlayerIndices in
// ../components/DraftResult (shared with KnockoutComplete).

export default function DraftComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { sport, viewOnly, username } = state;

  // Web only ever has one team per sport per day, so there's no pager — just
  // resolve the single team. A fresh completion passes draftedPlayers directly;
  // reopening a completed sport passes `entries` (we take the most recent).
  const team = useMemo(() => {
    if (state.entries && state.entries.length) return state.entries[0];
    if (state.draftedPlayers) {
      return { draftedPlayers: state.draftedPlayers, draftPositions: state.draftPositions };
    }
    return null;
  }, [state]);

  useEffect(() => {
    if (!sport || !team) navigate("/");
  }, [sport, team, navigate]);

  if (!sport || !team) return null;

  const { draftedPlayers, draftPositions } = team;

  // Position labels for display. Football keeps the resolved label (its flex
  // slots intentionally show the player's real position); every other sport
  // shows the fixed roster slot from SPORT_CONFIG — so e.g. baseball's DH always
  // reads "DH" rather than the player's natural position, regardless of how the
  // incoming positions were resolved.
  const slotConfig = SPORT_CONFIG[sport?.id]?.positions || [];
  const displayPositions = (draftPositions || []).map((slot, i) =>
    sport?.id === "football" ? slot : (slotConfig[i] ?? slot)
  );

  const teamRating   = calculateTeamRatingWithChemistry(draftedPlayers);
  const baseRating   = calculateTeamRating(draftedPlayers);
  const chemistry    = getChemistryBreakdown(draftedPlayers);
  const hasChemistry = chemistry.totalChemistryPoints > 0;

  return (
    <DraftCompleteContent
      sport={sport}
      draftedPlayers={draftedPlayers}
      draftPositions={displayPositions}
      teamRating={teamRating}
      baseRating={baseRating}
      chemistry={chemistry}
      hasChemistry={hasChemistry}
      viewOnly={viewOnly}
      username={username}
      navigate={navigate}
    />
  );
}

function DraftCompleteContent({
  sport, draftedPlayers, draftPositions,
  teamRating, baseRating, chemistry, hasChemistry, viewOnly, username, navigate,
}) {
  const { user, isGuest, freePlay } = useAuth();
  const [hasAppeared, setHasAppeared] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(viewOnly ? "already_played" : "idle"); // "idle"|"submitting"|"submitted"|"error"|"already_played"
  const [shareLabel, setShareLabel] = useState("SHARE");

  // Team-logo URL map: load once and bump so the roster cards re-render and
  // resolve their logos. The share capture awaits the same load itself, so it
  // doesn't depend on this tick (avoids generating the image twice).
  const [, bumpLogos] = useState(0);
  useEffect(() => {
    loadTeamLogos().then(() => bumpLogos(n => n + 1)).catch(() => {});
  }, []);

  // ── Shareable result image ──
  // Captured from a wrapper (captureRef) around the live result content. The
  // action buttons render OUTSIDE that wrapper, so the capture naturally ends at
  // the high/low section — no blank space below and no buttons in the image, with
  // no need to mutate or flicker the live view. (An off-screen clone rendered
  // blank in html-to-image; an in-tree node captures reliably.) Generated on load
  // so the share tap is instant. We force the team logos on for the shot (they
  // hide on narrow viewports) and wait for fonts/images/animations first.
  const captureRef = useRef(null);
  const shareFileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function generate() {
      try {
        await loadTeamLogos().catch(() => {});
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
        // Let the staggered entrance animations finish (max ~1s) so nothing is
        // captured mid-fade.
        await new Promise(r => setTimeout(r, 1200));
        const node = captureRef.current;
        if (cancelled || !node) return;
        // Wait for every image (wordmark + logos) to load AND decode first —
        // decoding matters on mobile Safari, which otherwise embeds them blank.
        const imgs = Array.from(node.querySelectorAll("img"));
        await Promise.all(imgs.map(async (img) => {
          if (!(img.complete && img.naturalWidth > 0)) {
            await new Promise(res => {
              img.addEventListener("load", res, { once: true });
              img.addEventListener("error", res, { once: true });
            });
          }
          if (img.decode) { try { await img.decode(); } catch {} }
        }));
        if (cancelled || !node) return;
        // Force the logos visible just for the capture, then restore.
        node.classList.add("rb-capturing");
        let blob = null;
        try {
          // html-to-image often omits images on the FIRST pass on mobile Safari
          // (the embedded data URLs aren't ready yet) — the wordmark and logos
          // come out blank. Running a few passes and keeping the last warms up
          // the embedding so they render. Cheap since this is pre-generated.
          const opts = { pixelRatio: 2, backgroundColor: "#0e1117" };
          for (let pass = 0; pass < 3; pass++) {
            blob = await toBlob(node, opts);
            if (cancelled) return;
          }
        } finally {
          node.classList.remove("rb-capturing");
        }
        if (cancelled || !blob) return;
        shareFileRef.current = new File([blob], `rosterable-${sport.id}-draft.png`, { type: "image/png" });
      } catch (err) {
        console.warn("Share image generation failed:", err);
      }
    }
    generate();
    return () => { cancelled = true; };
  }, [sport.id]);

  // Share the result. Prefers the image (mobile share sheet), then falls back to
  // downloading the PNG (desktop), then to copying text — so every browser does
  // something useful and nothing throws on cancel / unsupported APIs.
  async function handleShare() {
    const file = shareFileRef.current;

    // 1) Native share with the image (mobile).
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Rosterable" });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return; // user cancelled
        // otherwise fall through to download
      }
    }

    // 2) Download the PNG (desktop / no file-share support).
    if (file) {
      try {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setShareLabel("SAVED!");
        setTimeout(() => setShareLabel("SHARE"), 1500);
        return;
      } catch {
        // fall through to text
      }
    }

    // 3) Text fallback (image not ready / generation failed).
    const emoji = SPORT_EMOJI[sport.id] || "🏆";
    const text =
      `ROSTERABLE\n` +
      `${emoji} ${sport.name.toUpperCase()} DRAFTS ${emoji}\n` +
      `Team Rating: ${teamRating}\n` +
      `Chemistry: ${chemDisplayText}\n\n` +
      `Daily roster building at Rosterableapp.com`;

    if (navigator.share && (!navigator.canShare || navigator.canShare({ text }))) {
      try {
        await navigator.share({ title: "Rosterable", text });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        return;
      }
    }
    setShareLabel("COPIED!");
    setTimeout(() => setShareLabel("SHARE"), 1500);
  }

  useEffect(() => {
    const t = setTimeout(() => setHasAppeared(true), 120);
    return () => clearTimeout(t);
  }, []);

  // ─── Submit score to Firestore ──────────────────────────────────────────
  useEffect(() => {
    if (viewOnly) return;          // viewing existing drafts — never re-submit
    if (!user || isGuest || freePlay) return;  // guests + Free Play don't submit

    async function submit() {
      setSubmitStatus("submitting");
      try {
        const alreadyPlayed = await hasSubmittedDraftToday(user.uid, sport.id);
        if (alreadyPlayed) {
          setSubmitStatus("already_played");
          return;
        }
        await submitDraftScore({
          uid: user.uid,
          username: user.username,
          sport,
          draftedPlayers,
          draftPositions,
          teamRating,
          baseRating,
          chemistryBonus: chemistry.totalChemistryPoints,
        });
        setSubmitStatus("submitted");
      } catch (err) {
        console.error("Score submission failed:", err);
        setSubmitStatus("error");
      }
    }
    submit();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // High/Low players
  const highPlayer = draftedPlayers.reduce((best, p, i) =>
    p.overallRating > (best?.player.overallRating ?? -1)
      ? { player: p, position: draftPositions[i] } : best, null);
  const lowPlayer = draftedPlayers.reduce((best, p, i) =>
    p.overallRating < (best?.player.overallRating ?? 999)
      ? { player: p, position: draftPositions[i] } : best, null);

  const chemDisplayText = hasChemistry ? `+${chemistry.totalChemistryPoints}` : "+0";

  return (
    <div style={styles.page}>
      {/* Roster logos appear only when the play surface is wide enough; below
          the frame width they hide. The share capture (.rb-capturing) forces them on. */}
      <style>{`
        @media (max-width: 480px) { .rb-roster-logo { display: none; } }
        .rb-capturing .rb-roster-logo { display: flex !important; }
      `}</style>

      {/* Ambient sport glow */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        background: `radial-gradient(ellipse at top left, ${sport.hex}10, transparent 60%)`,
      }} />

      <div style={styles.scroll}>
        {/* Everything above the action buttons is wrapped here; the share image
            captures this node, so it ends at high/low with no blank bottom. */}
        <div ref={captureRef} style={styles.captureBlock}>
        {/* ── Header ── */}
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => navigate("/")}>‹</button>
          <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
          <div style={{ width: 44 }} />
        </div>

        {/* Drafter credit — only present for showcased rosters (passed via state).
            Sits inside the capture wrapper, so it appears in the share image too. */}
        {username && (
          <div style={styles.drafterTag}>DRAFTED BY {String(username).toUpperCase()}</div>
        )}

        {/* ── Submission status badge ── */}
        {submitStatus === "submitted" && (
          <div style={{ ...styles.statusBadge, background: "rgba(76,175,61,0.15)", borderColor: "#4caf7d66" }}>
            <span>✅</span><span style={styles.statusText}>Score submitted to leaderboard</span>
          </div>
        )}
        {submitStatus === "error" && (
          <div style={{ ...styles.statusBadge, background: "rgba(242,92,92,0.1)", borderColor: "#f25c5c44" }}>
            <span>⚠️</span><span style={styles.statusText}>Score couldn't be submitted</span>
          </div>
        )}
        {(submitStatus === "idle" || submitStatus === "submitting") && !isGuest && !freePlay && user && (
          <div style={{ ...styles.statusBadge, background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)" }}>
            <span style={{ opacity: 0.5 }}>⏳</span>
            <span style={{ ...styles.statusText, opacity: 0.5 }}>Submitting score...</span>
          </div>
        )}

        {/* ── Team Rating Section ── */}
        <TeamRatingSection
          sport={sport}
          teamRating={teamRating}
          players={draftedPlayers}
          hasAppeared={hasAppeared}
          sportLabel={`${sport.name.toUpperCase()} DRAFTS`}
        />

        {/* ── Condensed Roster Grid ── */}
        <div style={{ ...styles.section, padding: "6px" }}>
          <div style={styles.rosterGrid}>
            {getOrderedPlayerIndices(sport.id, draftedPlayers, draftPositions).map(idx => (
              <RosterCardCondensed
                key={idx}
                player={draftedPlayers[idx]}
                position={draftPositions[idx]}
                sport={sport}
                index={idx}
                hasAppeared={hasAppeared}
              />
            ))}
          </div>
        </div>

        {/* ── High / Low Section ── */}
        {highPlayer && lowPlayer && (
          <div style={{
            ...styles.section,
            padding: "6px",
            opacity: hasAppeared ? 1 : 0,
            transform: hasAppeared ? "translateX(0)" : "translateX(30px)",
            transition: "all 0.25s ease 0.6s",
          }}>
            <div style={styles.highLowHeader}>
              <span style={styles.highLowHeaderText}>HIGH / LOW PLAYER</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <HighLowRow player={highPlayer.player} position={highPlayer.position} sport={sport} isHighest={true} />
              <HighLowRow player={lowPlayer.player} position={lowPlayer.position} sport={sport} isHighest={false} />
            </div>
          </div>
        )}

        </div>{/* end share-capture wrapper */}

        {/* ── Action Buttons ── */}
        <div
          style={{
          ...styles.actionsSection,
          opacity: hasAppeared ? 1 : 0,
          transition: "opacity 0.4s ease 0.55s",
        }}>
          {/* Primary: Draft Again — hidden when viewing completed drafts */}
          {!viewOnly && (
            <ActionButton
              label="DRAFT AGAIN"
              icon="＋"
              isPrimary
              sportColor={sport.hex}
              onClick={() => navigate("/draft", { state: { sport } })}
            />
          )}

          {/* 2x2 grid */}
          <div style={styles.actionGrid}>
            <ActionButton label={shareLabel} icon="↑" onClick={handleShare} />
            <ActionButton
              label="LEADERBOARD"
              iconSrc={rankingIcon}
              onClick={() => navigate("/leaderboard", { state: { sport } })}
            />
            <ActionButton label="HOME" iconSrc={houseIcon} onClick={() => navigate("/")} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── High/Low Row ─────────────────────────────────────────────────────────────
// Mirrors DraftHighLowRow.swift

function HighLowRow({ player, position, sport, isHighest }) {
  return (
    <div style={styles.highLowRow}>
      <div style={{
        ...styles.highLowBadge,
        background: isHighest ? `${sport.hex}dd` : "rgba(255,255,255,0.15)",
      }}>
        <span style={{ fontSize: 10, color: isHighest ? "#000" : "#fff" }}>
          {isHighest ? "↑" : "↓"}
        </span>
      </div>
      <span style={styles.highLowPos}>{position}</span>
      <span style={styles.highLowName}>{player.name}</span>
      <span style={{ ...styles.highLowRating, color: isHighest ? sport.hex : "rgba(255,255,255,0.5)" }}>
        {player.overallRating}
      </span>
    </div>
  );
}

// ─── Action Button ────────────────────────────────────────────────────────────
// Mirrors GridButton.swift

function ActionButton({ label, icon, iconSrc, isPrimary, sportColor, onClick }) {
  const [isPressed, setIsPressed] = useState(false);
  return (
    <button
      style={{
        ...styles.actionBtn,
        background: isPrimary ? sportColor : "rgba(255,255,255,0.06)",
        border: isPrimary ? "none" : "1px solid rgba(255,255,255,0.1)",
        boxShadow: isPrimary ? `0 4px 12px ${sportColor}59` : "none",
        transform: isPressed ? "scale(0.96)" : "scale(1)",
        transition: "transform 0.1s ease",
        flex: isPrimary ? undefined : 1,
        width: isPrimary ? "100%" : undefined,
      }}
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {iconSrc ? (
        // Solid-black source SVG → black on the colored primary, white otherwise.
        <img
          src={iconSrc}
          alt=""
          style={{
            width: 17,
            height: 17,
            filter: isPrimary ? "brightness(0)" : "brightness(0) invert(1)",
            opacity: isPrimary ? 1 : 0.85,
          }}
        />
      ) : (
        <span style={{ fontSize: 13, color: isPrimary ? "#000" : "#fff" }}>{icon}</span>
      )}
      <span style={{
        ...styles.actionBtnLabel,
        color: isPrimary ? "#000" : "rgba(255,255,255,0.85)",
      }}>
        {label}
      </span>
    </button>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    borderRadius: "10px",
    border: "1px solid",
  },
  statusText: {
    fontSize: "13px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.75)",
  },
  drafterTag: {
    textAlign: "center",
    margin: "2px 0 4px",
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "1px",
    color: "rgba(255,255,255,0.6)",
  },
  page: {
    minHeight: "100vh",
    position: "relative",
  },
  scroll: {
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 6px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    position: "relative",
    zIndex: 1,
  },
  // Wraps the result content (everything except the action buttons) so the share
  // capture targets exactly this node. Mirrors scroll's column/gap so on-screen
  // spacing is identical.
  captureBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "34px",
    paddingBottom: "12px",
    paddingLeft: "6px",
    paddingRight: "6px",
  },
  backBtn: {
    background: "none",
    color: "rgba(255,255,255,0.8)",
    fontSize: "28px",
    fontWeight: "300",
    lineHeight: 1,
    width: 44,
    textAlign: "left",
    padding: 0,
  },
  wordmark: { height: "21px", width: "auto" },

  section: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "10px",
  },

  // Hero row
  heroRow: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },

  // Circular score
  circleCenter: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  circleRating: {
    fontSize: "28px",
    fontWeight: "800",
    lineHeight: 1,
    display: "inline-block",
  },
  circleScoreLabel: {
    fontSize: "9px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "2px",
    marginTop: "1px",
  },

  // Chemistry panel
  chemPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    minWidth: 0,
  },
  sportLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  sportLabelText: {
    fontSize: "13px",
    fontWeight: "800",
    color: "#ffffff",
    letterSpacing: "1.2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rule: {
    height: "1px",
    opacity: 0.5,
    borderRadius: "1px",
  },
  bonusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "20px",
  },
  bonusLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.75)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  bonusPts: {
    fontSize: "12px",
    fontWeight: "700",
    flexShrink: 0,
    marginLeft: "6px",
  },
  noChemLabel: {
    fontSize: "12px",
    fontWeight: "600",
    fontStyle: "italic",
    color: "rgba(255,255,255,0.3)",
  },
  chemTotalRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  chemTotalLabel: {
    fontSize: "10px",
    fontWeight: "800",
    color: "#ffffff",
    letterSpacing: "1.2px",
  },
  chemTotalBadge: {
    fontSize: "11px",
    fontWeight: "800",
    color: "#000000",
    padding: "3px 7px",
    borderRadius: "5px",
  },

  // Roster grid
  rosterGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px",
  },
  rosterCard: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: "10px",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minHeight: "44px",
    // Lets the 1fr grid tracks stay equal width regardless of name length;
    // without this a long unbreakable name forces its track wider than its pair.
    minWidth: 0,
  },
  rosterPillStack: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flexShrink: 0,
  },
  rosterPosPill: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#000",
    padding: "1px 4px",
    borderRadius: "4px",
    textAlign: "center",
    minWidth: "30px",
  },
  rosterYearPill: {
    fontSize: "10px",
    fontWeight: "400",
    color: "rgba(255,255,255,0.7)",
    background: "rgba(255,255,255,0.08)",
    padding: "1px 4px",
    borderRadius: "4px",
    textAlign: "center",
    minWidth: "30px",
  },
  rosterNameBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    flex: 1,
    minWidth: 0,
  },
  rosterName: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#fff",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    letterSpacing: "0.2px",
  },
  // Team logo for the condensed roster card — mirrors PlayerCard's treatment,
  // sized down to align with the position/year pill stack.
  rosterLogo: {
    width: "30px",
    height: "30px",
    flexShrink: 0,
    background: "#c2c2c2",
    borderRadius: "6px",
    border: "1px solid #8c8c8c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: "2px",
    boxSizing: "border-box",
  },
  rosterLogoImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    display: "block",
  },
  // Soccer: landscape flag, no background.
  rosterLogoSoccer: {
    width: "30px",
    height: "20px",
    flexShrink: 0,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  rosterLogoImgSoccer: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  // High/Low
  highLowHeader: {
    paddingBottom: "6px",
    paddingLeft: "2px",
  },
  highLowHeaderText: {
    fontSize: "11px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "1.5px",
  },
  highLowRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "6px 8px",
    background: "rgba(255,255,255,0.04)",
    borderRadius: "8px",
  },
  highLowBadge: {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  highLowPos: {
    fontSize: "10px",
    fontWeight: "700",
    color: "#fff",
    background: "rgba(255,255,255,0.12)",
    padding: "3px 6px",
    borderRadius: "4px",
    minWidth: "28px",
    textAlign: "center",
  },
  highLowName: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#fff",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  highLowRating: {
    fontSize: "13px",
    fontWeight: "700",
    flexShrink: 0,
  },

  // Actions
  actionsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    paddingTop: "4px",
  },
  actionGrid: {
    display: "flex",
    gap: "8px",
  },
  actionBtn: {
    height: "52px",
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
    cursor: "pointer",
    padding: "0 8px",
  },
  actionBtnLabel: {
    fontSize: "10px",
    fontWeight: "800",
    letterSpacing: "0.5px",
  },
};
